import type { BindSiteAssetsInput, SiteAssetKind } from "@crm/domain/site-inputs";
import { useAtomValue } from "@effect/atom-react";
import type { CapabilityId } from "@crm/domain/capabilities";
import { rpcEffectCaller } from "@crm/rpc-client";
import { SitesRpc } from "@crm/rpc-contract/groups/sites";
import { queryAtomEffect } from "@crm/shared/query-atom/effect";
import { useMutationCommand } from "@crm/ui/hooks/use-mutation-command";
import { Effect, Option, Scope } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";

// 类型化 RPC Effect 调用器（错误通道保留；Scope 由客户端 runtime 终端提供）
const sites = rpcEffectCaller(SitesRpc);

/**
 * 站点查询域（对应 workbenchSiteKeys，Effect-native）。
 *
 * 我的站点 / 能力集 / 全部站点 mutation 共用同一响应键 `"workbenchSite"`：
 * 任一 mutation 成功后经 reactivityKeys 驱动本域全部原子刷新。
 */
const mySitesAtom = queryAtomEffect((_key: readonly []) => sites.getMySites(undefined), {
  staleTime: 30_000,
  reactivityKeys: ["workbenchSite"],
});

/** 我的站点列表 + 活动站点 ID（workbench_sites 主干唯一查询入口，AsyncResult 消费） */
export function useMySites() {
  return useAtomValue(mySitesAtom([]));
}

/** 当前活动站点（无站点时为 null） */
export function useActiveSite() {
  const result = useMySites();
  const data = AsyncResult.value(result).pipe(Option.getOrUndefined);
  if (!data?.success) {
    return null;
  }
  return data.data.sites.find((s) => s.id === data.data.activeSiteId) ?? null;
}

const capabilitiesAtom = queryAtomEffect(
  (_key: readonly [userId: string]) => sites.getMyCapabilities(undefined),
  { staleTime: 5 * 60_000, reactivityKeys: ["workbenchSite"] },
);

/** 我的能力集（课程门禁）：导航/路由守卫共用 */
export function useMyCapabilities(userId: string | undefined): {
  capabilities: Set<CapabilityId>;
  isLoading: boolean;
  isFailure: boolean;
} {
  const result = useAtomValue(capabilitiesAtom([userId ?? "anonymous"]));
  const response = AsyncResult.value(result).pipe(Option.getOrUndefined);
  const list = response?.success ? response.data : [];
  return {
    capabilities: new Set(list),
    isLoading: AsyncResult.isWaiting(result),
    isFailure: AsyncResult.isFailure(result),
  };
}

/**
 * 站点 mutation 统一外壳（Effect-native）。
 *
 * 提交走 useMutationCommand；成功后经 reactivityKeys ["workbenchSite"]
 * 失效本域订阅者，跨页由 staleTime 兜底。重复提交 drop（返回 Dropped），绝不悬挂。
 */
function useSiteMutation<V, A, E>(
  message: string,
  run: (vars: V) => Effect.Effect<A, E, Scope.Scope>,
) {
  const { state, isPending, execute, executePromise, reset } = useMutationCommand<A, E>({
    message,
    reactivityKeys: [
      "workbenchSite",
      "acfContext",
      "sioAdGroup",
      "studentSioSession",
      "contentTopic",
      "topicTag",
      "sioExternalLinkSettings",
    ],
  });
  return {
    execute: (vars: V) => execute(run(vars)),
    executePromise: (vars: V) => executePromise(run(vars)),
    isPending,
    state,
    reset,
  };
}

export function useCreateSite() {
  return useSiteMutation(
    "创建站点失败",
    (input: { name: string; domain?: string; businessInfo?: string; customerInfo?: string }) =>
      sites.createSite(input),
  );
}

export function useUpdateSite() {
  return useSiteMutation(
    "更新站点失败",
    (input: {
      id: string;
      name?: string;
      domain?: string | null;
      businessInfo?: string;
      customerInfo?: string;
    }) => sites.updateSite(input),
  );
}

export function useSetActiveSite() {
  return useSiteMutation("切换活动站点失败", (id: string) => sites.setActiveSite({ id }));
}

export function useDeleteSite() {
  return useSiteMutation("删除站点失败", (id: string) => sites.deleteSite({ id }));
}

const unassignedAssetsAtom = queryAtomEffect(
  (key: readonly [SiteAssetKind]) => sites.getUnassignedSiteAssets({ kind: key[0] }),
  { reactivityKeys: ["workbenchSite"] },
);
export function useUnassignedSiteAssets(kind: SiteAssetKind) {
  return useAtomValue(unassignedAssetsAtom([kind]));
}
export function useBindSiteAssets() {
  return useSiteMutation("归档失败", (input: BindSiteAssetsInput) => sites.bindSiteAssets(input));
}
